import type { ComponentType } from 'preact'
import { Video, Mail, Globe, FileText, Share2, Film, BookOpen, File } from 'lucide-preact'
import { BRAND_CLASSES } from '@/constants/theme'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type IconType = ComponentType<any>

export const ASSET_TYPE_META: Record<string, { Icon: IconType; color: string; badgeBg: string }> = {
  'Animated Promotional Video': { Icon: Video, color: BRAND_CLASSES.ACCENT.VIOLET, badgeBg: 'bg-violet-100 dark:bg-violet-900/30' },
  'Landing Page': { Icon: Globe, color: BRAND_CLASSES.ACCENT.TEAL, badgeBg: 'bg-teal-100 dark:bg-teal-900/30' },
  'LinkedIn Asset': { Icon: Share2, color: BRAND_CLASSES.ACCENT.BLUE, badgeBg: 'bg-blue-100 dark:bg-blue-900/30' },
  'LinkedIn Event Asset': { Icon: Share2, color: BRAND_CLASSES.ACCENT.BLUE, badgeBg: 'bg-blue-100 dark:bg-blue-900/30' },
  'Slide Deck': { Icon: FileText, color: BRAND_CLASSES.ACCENT.AMBER, badgeBg: 'bg-amber-100 dark:bg-amber-900/30' },
  'Webinar Recording': { Icon: Video, color: BRAND_CLASSES.ACCENT.VIOLET, badgeBg: 'bg-violet-100 dark:bg-violet-900/30' },
  'Highlight Snippet': { Icon: Film, color: BRAND_CLASSES.ACCENT.RED, badgeBg: 'bg-red-100 dark:bg-red-900/30' },
  'Lead Magnet': { Icon: FileText, color: BRAND_CLASSES.ACCENT.AMBER, badgeBg: 'bg-amber-100 dark:bg-amber-900/30' },
  'Email Copy': { Icon: Mail, color: BRAND_CLASSES.ACCENT.INDIGO, badgeBg: 'bg-indigo-100 dark:bg-indigo-900/30' },
  'Email Template': { Icon: Mail, color: BRAND_CLASSES.ACCENT.PURPLE, badgeBg: 'bg-purple-100 dark:bg-purple-900/30' },
  'Case Study': { Icon: BookOpen, color: BRAND_CLASSES.ACCENT.TEAL, badgeBg: 'bg-teal-100 dark:bg-teal-900/30' },
  'Other': { Icon: File, color: BRAND_CLASSES.ACCENT.SLATE, badgeBg: 'bg-slate-100 dark:bg-slate-700/50' },
}

export const DEFAULT_ASSET_META = { Icon: File, color: BRAND_CLASSES.ACCENT.SLATE, badgeBg: 'bg-slate-100 dark:bg-slate-700/50' }

/**
 * ORB-457: fixed display order for the web asset list, mirroring the campaign
 * lifecycle instead of an alphabetical-by-name scatter:
 *   Landing Page → emails → LinkedIn / social → post-event (recording / snippet) → everything else.
 * Lower rank sorts first. Keys are the exact wire-format asset-type labels the API
 * serializes (see ASSET_TYPE_META); any type not listed here falls to the end via
 * ASSET_TYPE_ORDER_FALLBACK and then sorts among its peers by name.
 */
export const ASSET_TYPE_ORDER: Record<string, number> = {
  'Landing Page': 0,
  'Email Template': 1,
  'Email Copy': 2,
  'LinkedIn Asset': 3,
  'LinkedIn Event Asset': 4,
  'Webinar Recording': 5,
  'Highlight Snippet': 6,
}

/** Rank assigned to any asset type absent from ASSET_TYPE_ORDER — sorts to the end. */
export const ASSET_TYPE_ORDER_FALLBACK = 100

/** Sort rank for an asset type. Lower sorts first; unknown / unlisted types sort last. */
export function getAssetTypeRank(type: string): number {
  return ASSET_TYPE_ORDER[type] ?? ASSET_TYPE_ORDER_FALLBACK
}

/**
 * Comparator for the fixed asset-type order: rank first (getAssetTypeRank), then the type
 * label, then a case-insensitive alphabetical sort by name. The type-label tiebreaker keeps
 * assets of the same type grouped even when several types share the fallback rank (the
 * "everything else" bucket) — without it, unlisted types would interleave by name. Used both
 * to pre-sort the loaded asset list and as the Type column's sort in the asset grid.
 */
export function compareAssetsByTypeThenName(
  a: { Type: string; Name: string },
  b: { Type: string; Name: string },
): number {
  const rankDelta = getAssetTypeRank(a.Type) - getAssetTypeRank(b.Type)
  if (rankDelta !== 0) return rankDelta
  const typeDelta = a.Type.localeCompare(b.Type, undefined, { sensitivity: 'base' })
  if (typeDelta !== 0) return typeDelta
  return a.Name.localeCompare(b.Name, undefined, { sensitivity: 'base' })
}

/** Asset types that support the Webster builder overlay */
export const WEBSTER_ASSET_TYPES = ['Landing Page', 'Email Template', 'Email Copy'] as const
export type WebsterAssetTypeName = (typeof WEBSTER_ASSET_TYPES)[number]

export function isWebsterAssetType(type: string): boolean {
  return (WEBSTER_ASSET_TYPES as readonly string[]).includes(type)
}

export const ASSET_STATUS_COLORS: Record<string, string> = {
  'Draft': BRAND_CLASSES.STATUS.DEFAULT,
  'Ready for Sign-Off': BRAND_CLASSES.STATUS.PENDING,
  'Signed Off': BRAND_CLASSES.STATUS.SUCCESS,
  'Feedback Submitted': BRAND_CLASSES.STATUS.DRAFT,
  'Reopened': BRAND_CLASSES.STATUS.INFO,
}
